import { motion } from 'framer-motion'
import { Calendar, Clock } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { Badge, getStatusVariant } from '@/components/ui/Badge'
import { getStageLabel } from './deals-constants'
import type { Deal } from '@/types'

type DealsCalendarDayListProps = {
  heading: string
  deals: Deal[]
  onSelectDeal: (deal: Deal) => void
}

export function DealsCalendarDayList({
  heading,
  deals,
  onSelectDeal,
}: DealsCalendarDayListProps) {
  if (deals.length === 0) {
    return (
      <div className='glass-card p-8 text-center'>
        <Calendar size={32} className='mx-auto mb-2 text-text-label opacity-40' />
        <p className='text-sm text-text-label'>Nenhum deal neste dia</p>
        <p className='mt-1 text-xs text-text-muted'>
          Selecione outro dia no calendário ou mude de mês
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className='mb-3 flex items-center gap-3'>
        <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10'>
          <Calendar size={14} className='text-accent' />
        </div>
        <h3 className='text-sm font-semibold capitalize text-text-primary'>{heading}</h3>
        <span className='rounded-full bg-white/[0.06] px-2 py-0.5 text-xs text-text-label'>
          {deals.length} {deals.length === 1 ? 'deal' : 'deals'}
        </span>
      </div>

      <div className='space-y-2'>
        {deals.map((deal, i) => (
          <motion.div
            key={deal.id}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.03, duration: 0.2 }}
            onClick={() => onSelectDeal(deal)}
            className='glass-card flex cursor-pointer items-center justify-between gap-4 p-4 transition-all duration-150 hover:border-accent/40'
          >
            <div className='flex min-w-0 items-center gap-3'>
              <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10'>
                <span className='text-xs font-semibold text-accent'>
                  {deal.empresaNome?.[0] || '?'}
                </span>
              </div>
              <div className='min-w-0'>
                <p className='truncate text-sm font-medium text-text-primary'>
                  {deal.empresaNome}
                </p>
                <p className='truncate text-xs text-text-label'>{deal.nome}</p>
              </div>
            </div>

            <div className='flex shrink-0 items-center gap-4'>
              {deal.dataReuniao && (
                <div className='flex items-center gap-1.5 text-text-label'>
                  <Clock size={12} />
                  <span className='text-xs'>
                    {new Date(deal.dataReuniao).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}
              <span className='text-sm font-semibold text-text-primary'>
                {formatCurrency(deal.valor)}
              </span>
              <Badge variant={getStatusVariant(deal.etapa)}>
                {getStageLabel(deal.etapa)}
              </Badge>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}
